import { Token } from '@kodiak-finance/sdk-core'
import { SearchInput } from 'components/SearchModal/styleds'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/Select'
import { useTokenInfo } from 'lib/hooks/kodiakFarms/useTokenInfo'
import { useMemo, useState } from 'react'
import { Text } from 'rebass'
import styled from 'styled-components'

interface Props {
  value: string
  onChange: (value: string) => void
  placeholder: string
  customPlaceholder: string
  allTokens: {
    [address: string]: Token
  }
  disabled?: boolean | null
}

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.5rem;
`

const TokenItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TokenLogo = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`
const TokenSymbol = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`

const TokenName = styled.span`
  color: ${({ theme }) => theme.textSecondary};
`

export const CustomInput = styled(SearchInput)`
  height: 40px;
  padding: 8px;
`

const TokenDisplay = ({ token }: { token: any }) => (
  <TokenItemContent>
    {token.logoURI && <TokenLogo src={token.logoURI} alt={token.symbol} />}
    <TokenInfo>
      <TokenSymbol>{token.symbol}</TokenSymbol>
      <TokenName>- {token.name}</TokenName>
    </TokenInfo>
  </TokenItemContent>
)

export const RewardTokenSelector = ({
  value,
  onChange,
  placeholder,
  allTokens,
  customPlaceholder,
  disabled,
}: Props): JSX.Element => {
  const { tokenInfo, error, fetchTokenInfo } = useTokenInfo()

  const [isCustom, setIsCustom] = useState<boolean>(() => {
    if (value && !allTokens[value]) {
      return true
    }
    return false
  })

  const [searchQuery, setSearchQuery] = useState('')

  const selectedToken =
    allTokens[value] ||
    (isCustom && tokenInfo
      ? {
          symbol: tokenInfo.symbol,
          name: tokenInfo.name,
          decimals: tokenInfo.decimals,
        }
      : null)

  const filteredTokens = useMemo(() => {
    return Object.values(allTokens)
      .filter((token) => {
        const search = searchQuery.toLowerCase()
        return (
          token.symbol?.toLowerCase().includes(search) ||
          token.name?.toLowerCase().includes(search) ||
          token.address.toLowerCase().includes(search)
        )
      })
      .sort((a, b) => (a.symbol || '').localeCompare(b.symbol || ''))
  }, [allTokens, searchQuery])

  const handleSelectChange = (newValue: string) => {
    if (newValue === 'custom') {
      setIsCustom(true)
      onChange('')
      setSearchQuery('')
    } else {
      setIsCustom(false)
      onChange(newValue)
      setSearchQuery('')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value
    onChange(address)
    if (address) fetchTokenInfo(address)
  }

  return (
    <Container>
      <Select value={isCustom ? 'custom' : value} onValueChange={handleSelectChange} disabled={disabled ?? null}>
        <SelectTrigger>
          <SelectValue>
            {selectedToken ? (
              <TokenDisplay token={selectedToken} />
            ) : isCustom ? (
              <span>Custom Token</span>
            ) : (
              <span>{placeholder}</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              padding: '8px',
              background: 'inherit',
            }}
          >
            <CustomInput
              placeholder="Search tokens..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
          </div>
          <SelectItem value="custom">Use Custom Token</SelectItem>
          {filteredTokens.map((token) => (
            <SelectItem key={token.address} value={token.address}>
              <TokenDisplay token={token} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isCustom && (
        <>
          <CustomInput
            placeholder={customPlaceholder}
            value={value}
            onChange={handleInputChange}
            pattern="^0x[a-fA-F0-9]{40}$"
            title="Please enter a valid Ethereum address"
          />
          {error ? (
            <Text fontSize={12} color="red">
              {error}
            </Text>
          ) : (
            tokenInfo && (
              <Text fontSize={12} color="textSecondary">
                {tokenInfo.name} ({tokenInfo.symbol})
              </Text>
            )
          )}
        </>
      )}
    </Container>
  )
}

export default RewardTokenSelector
